/**
 * AppCatalogPage — App-Katalog (Image Builder 2.1 Phase C, #1712).
 *
 * Every catalog app with its newest version and status (spec §5.1 mapping:
 * "Geprüft" = verified, "Pflicht" = required && verified, "In Prüfung" =
 * testing, "Fehlgeschlagen" = failed). Operators get the way into the
 * registration wizard; the API enforces the same role.
 */

import React from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { appsApi } from '../services/appsService';
import { AppSummary } from '../services/types';
import { useApiQuery } from '../hooks/useApiQuery';
import { useAuth } from '../contexts/AuthContext';
import { TableSkeleton } from '../components/SkeletonLoaders';
import ErrorMessage from '../components/ErrorMessage';

type ChipColor = 'success' | 'primary' | 'info' | 'error' | 'default' | 'warning';

export function catalogStatus(app: Pick<AppSummary, 'status' | 'required'>): { label: string; color: ChipColor } {
  switch (app.status) {
    case 'verified':
      return app.required ? { label: 'Pflicht', color: 'primary' } : { label: 'Geprüft', color: 'success' };
    case 'testing':
      return { label: 'In Prüfung', color: 'info' };
    case 'failed':
      return { label: 'Fehlgeschlagen', color: 'error' };
    case 'deprecated':
      return { label: 'Abgekündigt', color: 'warning' };
    default:
      return { label: 'Entwurf', color: 'default' };
  }
}

export default function AppCatalogPage() {
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const canRegister = hasRole('operator');
  const { data: apps, loading, error } = useApiQuery(() => appsApi.list(), []);

  if (loading) return <TableSkeleton rows={5} columns={4} />;

  const sorted = [...(apps ?? [])].sort((a, b) => a.name.localeCompare(b.name, 'de'));

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={3}>
        <Typography variant="h4" sx={{ flexGrow: 1 }}>
          App-Katalog
        </Typography>
        {canRegister && (
          <Button variant="contained" onClick={() => navigate('/apps/register')}>
            App registrieren
          </Button>
        )}
      </Box>

      {error && <ErrorMessage message={error} title="Katalog konnte nicht geladen werden" />}

      {!error && sorted.length === 0 && (
        <Alert severity="info">
          <Typography variant="body2">
            Noch keine App im Katalog.{' '}
            {canRegister ? (
              <RouterLink to="/apps/register">App registrieren</RouterLink>
            ) : (
              'Ein Operator kann Apps registrieren.'
            )}
          </Typography>
        </Alert>
      )}

      {sorted.length > 0 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Hersteller</TableCell>
                <TableCell>Version</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sorted.map((app) => {
                const s = catalogStatus(app);
                return (
                  <TableRow key={app.appId}>
                    <TableCell>
                      <Typography variant="body2">{app.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {app.appId}
                      </Typography>
                    </TableCell>
                    <TableCell>{app.vendor}</TableCell>
                    <TableCell>{app.latestVersion}</TableCell>
                    <TableCell>
                      <Chip size="small" label={s.label} color={s.color} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
